import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ExpoSpeechRecognitionModule, useSpeechRecognitionEvent } from 'expo-speech-recognition';
import { Ionicons } from '../components/icons';
import {
  Building2,
  Check,
  ChevronRight,
  CircleCheck,
  ClipboardType,
  FileText,
  Lightbulb,
  Lock,
  Plus,
  TriangleAlert,
  type LucideIcon,
} from '../components/icons';
import { Button } from '../components/Button';
import { OutputLanguagePicker } from '../components/OutputLanguagePicker';
import { FondNuit } from '../components/FondNuit';
import { MessageBubble } from '../components/MessageBubble';
import { FeedbackSection } from '../components/FeedbackSection';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { CarteInterlocuteur } from '../components/CarteInterlocuteur';
import { PiedAction } from '../components/PiedAction';
import { SectionRepliable } from '../components/SectionRepliable';
import { GradientButton } from '../components/GradientButton';
import { OutlineIconButton } from '../components/OutlineIconButton';
import { useInterview } from '../hooks/useInterview';
import { useFicheDePoste } from '../hooks/useFicheDePoste';
import { useVoixRecruteur } from '../hooks/useVoixRecruteur';
import { lireVoixRestante, type VoixRestante } from '../services/voixRecruteur';
import { preparerLecture } from '../services/lecteurVoix';
import { enregistrerPreferenceVoix, lirePreferenceVoix } from '../services/preferenceVoix';
import { exporterBilanPdf, pdfBilanDisponible } from '../services/feedbackPdf';
import { IndicateurVoix } from '../essais/IndicateurVoix';
import { estProduction } from '../config/canal';
import { listCvs, type CvSummary } from '../services/cv';
import { interviewPhaseLabel } from '../utils/interviewPhase';
import { colors, formes, layout, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { OUTPUT_LANGUAGES, type InterviewRole, type OutputLanguage } from '../types';
import { t } from '../i18n';
import { confirmer } from '../utils/confirmation';
import { styleMiroir } from '../i18n/sens';

export interface InterviewScreenProps {
  // Écran POUSSÉ depuis le sommaire Carrière : goBack() dépile réellement. C'était une racine
  // d'onglet jusqu'ici, sans pile — d'où l'absence de flèche, qui n'avait alors rien à dépiler.
  onBack?: () => void;
  // « Ajouter un autre CV » pousse « Mon CV », voisin dans la même pile. Sans câblage, la ligne
  // n'est pas rendue et l'écran reste montable hors navigateur.
  onAddCv?: () => void;
}

export function InterviewScreen({ onBack, onAddCv }: InterviewScreenProps = {}) {
  const interview = useInterview();

  if (interview.phase === 'chat') return <ChatView interview={interview} />;
  if (interview.phase === 'feedback') return <FeedbackView interview={interview} />;
  return <SetupView interview={interview} onBack={onBack} onAddCv={onAddCv} />;
}

type Interview = ReturnType<typeof useInterview>;

// ───────────── setup : interlocuteur, CV, poste visé ─────────────

// Trois façons EXCLUSIVES de dire le poste visé (le serveur accepte contexte OU offre, jamais
// besoin des deux) : l'intitulé tapé (défaut), une fiche importée, une offre collée.
type PosteMode = 'title' | 'file' | 'paste';

/**
 * Préfixe du CONTEXTE envoyé au prompt, volontairement figé en français.
 *
 * buildContext assemble « Type d'entreprise : startup » et l'envoie au serveur, où il rejoint un
 * prompt français. Le traduire ferait dépendre le CONTENU DU PROMPT de la langue de l'interface :
 * deux personnes préparant le même entretien enverraient deux contextes différents. La langue de
 * l'entretien PRODUIT se règle par le paramètre `language`, pas par là. Le LIBELLÉ affiché
 * au-dessus du champ, lui, est traduit — c'est de l'interface.
 */
const CONTEXT_COMPANY_PREFIX = "Type d'entreprise";

// Construit AU RENDU : constante de module = libellés figés à l'import. Les valeurs ('recruiter',
// 'manager') sont des clés qui partent au serveur, elles ne se traduisent jamais.
const interlocuteurs = (): { value: InterviewRole; label: string; tags: string }[] => [
  {
    value: 'recruiter',
    label: t('interview.interlocutors.recruiter.label'),
    tags: [
      t('interview.interlocutors.recruiter.points.motivation'),
      t('interview.interlocutors.recruiter.points.parcours'),
      t('interview.interlocutors.recruiter.points.competences'),
    ].join(' · '),
  },
  {
    value: 'manager',
    label: t('interview.interlocutors.manager.label'),
    tags: [
      t('interview.interlocutors.manager.points.terrain'),
      t('interview.interlocutors.manager.points.metiers'),
      t('interview.interlocutors.manager.points.situation'),
    ].join(' · '),
  },
];

// Bouton-icône du champ poste : fiche importée ou offre collée. Rappuyer sur l'actif revient à l'intitulé.
function BoutonPoste({
  icon: Icon,
  label,
  actif,
  onPress,
  testID,
}: {
  icon: LucideIcon;
  label: string;
  actif: boolean;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: actif }}
      onPress={onPress}
      style={[styles.boutonPoste, actif && styles.boutonPosteActif]}
    >
      <Icon size={20} color={actif ? colors.white : FEATURE_ACCENTS.interview.ink} strokeWidth={2.2} />
    </Pressable>
  );
}

function texteVoixRestante(v: VoixRestante): string {
  const periode = v.periode === 'semaine' ? 'semaine' : 'mois';
  const variante = v.restants === 0 ? 'Aucun' : v.restants === 1 ? 'Un' : '';
  return t(`interview.voixRestante.${periode}${variante}`, { n: v.restants });
}

function SetupView({
  interview,
  onBack,
  onAddCv,
}: {
  interview: Interview;
  onBack?: () => void;
  onAddCv?: () => void;
}) {
  const [cvs, setCvs] = useState<CvSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Liste des CV dépliée (« Changer ») : repliée, un seul CV est montré, celui qui sera utilisé.
  const [changerCv, setChangerCv] = useState(false);
  const [posteMode, setPosteMode] = useState<PosteMode>('title');
  const [context, setContext] = useState('');
  const [companyType, setCompanyType] = useState('');
  const [companyOuverte, setCompanyOuverte] = useState(false);
  const [role, setRole] = useState<InterviewRole>('recruiter');
  const [language, setLanguage] = useState<OutputLanguage>('fr');
  // Fiche de poste : logique partagée avec la notation et « Modifier mon CV ». Une sélection de
  // captures vide ne touche à rien ici, comme avant.
  const {
    texte: offerText,
    setTexte: setOfferText,
    fichier: offerFile,
    setFichier: setOfferFile,
    captures: offerImages,
    setCaptures: setOfferImages,
    presente: hasOffer,
    choisirFichier: pickOfferFile,
    ajouterCaptures: addOfferImages,
    construire: buildOffer,
  } = useFicheDePoste({ onErreur: setLocalError, capturesVidesIgnorees: true });
  // Vraie voix restante dans la période : affichée si on la connaît, rien sinon (jamais un nombre deviné).
  const [voixRestante, setVoixRestante] = useState<VoixRestante | null>(null);
  useEffect(() => {
    let vivant = true;
    void lireVoixRestante().then((v) => {
      if (vivant) setVoixRestante(v);
    });
    return () => {
      vivant = false;
    };
  }, []);

  useEffect(() => {
    listCvs()
      .then((list) => {
        setCvs(list);
        if (list[0]) setSelectedId(list[0].id);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : t('interview.loadError')));
  }, []);

  const selected = cvs?.find((c) => c.id === selectedId) ?? null;

  // Changer d'entrée VIDE les autres : le serveur n'a besoin que d'une seule source de poste, et
  // laisser traîner une offre invisible derrière un intitulé tapé ensuite serait un piège.
  // Rappuyer sur l'entrée active revient à l'intitulé.
  const choosePoste = (mode: Exclude<PosteMode, 'title'>) => {
    const suivant: PosteMode = mode === posteMode ? 'title' : mode;
    setLocalError(null);
    setPosteMode(suivant);
    if (suivant !== 'paste') setOfferText('');
    if (suivant !== 'file') {
      setOfferFile(null);
      setOfferImages([]);
    }
    if (suivant !== 'title') setContext('');
  };

  // Contexte texte : intitulé du poste + type d'entreprise. L'offre voyage à part (extraite au
  // start). Le type d'entreprise enrichit le contexte MÊME quand le poste vient d'une offre : les
  // questions d'une startup et d'un grand groupe diffèrent réellement.
  const buildContext = () =>
    [context.trim(), companyType.trim() && `${CONTEXT_COMPANY_PREFIX} : ${companyType.trim()}`]
      .filter(Boolean)
      .join('\n\n');

  // Le type d'entreprise seul ne suffit PAS : ce n'est pas un poste visé. Il complète, il ne remplace.
  const canStart = !!selected && (context.trim().length > 0 || hasOffer);

  const begin = async () => {
    if (!canStart) return;
    // `buildOffer` lit la fiche de poste importée : sans cette garde, un fichier illisible
    // interrompait le démarrage sans un mot, et le bouton paraissait mort.
    let offer;
    try {
      offer = await buildOffer();
    } catch (e) {
      return setLocalError(e instanceof Error ? e.message : t('interview.startFailed'));
    }
    setLocalError(null);
    interview.start(JSON.stringify(selected!.content), buildContext(), role, offer, language);
  };

  const peutChanger = !!cvs && (cvs.length > 1 || (cvs.length > 0 && !!onAddCv));
  const erreur = localError ?? interview.error;

  const { large } = useGabarit();
  const sRole = (
    <>
          <CarteInterlocuteur testID="iv-role" options={interlocuteurs()} value={role} onChange={setRole} />

              </>
  );
  const sCv = (
    <>
{/* ─── CV utilisé ─── */}
          <Text style={styles.etiquette}>{t('interview.cvUsed')}</Text>
          <View style={styles.carte}>
            {cvs === null && !loadError ? (
              <ActivityIndicator testID="iv-loading" color={FEATURE_ACCENTS.interview.ink} />
            ) : null}
            {loadError ? (
              <Text testID="iv-load-error" style={styles.error}>
                {loadError}
              </Text>
            ) : null}
            {cvs && cvs.length === 0 ? (
              <Text testID="iv-empty" style={styles.vide}>
                {t('interview.noCv')}
              </Text>
            ) : null}

            {selected ? (
              <View testID="iv-cv-courant" style={styles.cvLigne}>
                <View style={styles.cvIcone}>
                  <FileText size={18} color={FEATURE_ACCENTS.interview.ink} strokeWidth={2} />
                </View>
                <Text style={styles.cvNom} numberOfLines={1}>
                  {selected.title}
                </Text>
                {peutChanger ? (
                  <Pressable
                    testID="iv-cv-change"
                    accessibilityRole="button"
                    accessibilityLabel={t('interview.changeA11y')}
                    accessibilityState={{ expanded: changerCv }}
                    onPress={() => setChangerCv((v) => !v)}
                    style={styles.changer}
                  >
                    <Text style={styles.changerTexte}>{t('interview.change')}</Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}

            {changerCv
              ? cvs?.map((cv) => {
                  const active = cv.id === selectedId;
                  return (
                    <Pressable
                      key={cv.id}
                      testID={`iv-cv-${cv.id}`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      onPress={() => {
                        setSelectedId(cv.id);
                        setChangerCv(false);
                      }}
                      style={[styles.cvLigne, styles.cvLigneListe]}
                    >
                      <Text style={styles.cvNom} numberOfLines={1}>
                        {cv.title}
                      </Text>
                      <View style={[styles.radio, active && styles.radioOn]}>
                        {active ? <Check size={12} color={colors.white} strokeWidth={3} /> : null}
                      </View>
                    </Pressable>
                  );
                })
              : null}

            {onAddCv && cvs !== null && (changerCv || cvs.length === 0) ? (
              <Pressable
                testID="iv-add-cv"
                accessibilityRole="button"
                onPress={onAddCv}
                style={[styles.cvLigne, cvs.length > 0 && styles.cvLigneListe]}
              >
                <View style={styles.cvIcone}>
                  <Plus size={18} color={FEATURE_ACCENTS.interview.ink} strokeWidth={2} />
                </View>
                <Text style={styles.ajouterCv}>{t('interview.addCv')}</Text>
                <View style={styleMiroir()}><ChevronRight size={18} color={colors.grey400} strokeWidth={2} /></View>
              </Pressable>
            ) : null}
          </View>

              </>
  );
  const sPoste = (
    <>
{/* ─── Poste visé ─── */}
          <Text style={styles.etiquette}>{t('interview.posteSection')}</Text>
          <View style={styles.carte}>
            <View style={styles.posteLigne}>
              {posteMode === 'title' ? (
                <TextInput
                  testID="iv-context"
                  style={[styles.champ, styles.champFlex]}
                  placeholder={t('interview.posteExample')}
                  placeholderTextColor={colors.grey500}
                  value={context}
                  onChangeText={setContext}
                />
              ) : (
                <View style={[styles.champ, styles.champFlex, styles.champMode]}>
                  <Text style={styles.champModeTexte} numberOfLines={1}>
                    {posteMode === 'file' ? t('interview.posteFile') : t('interview.postePaste')}
                  </Text>
                </View>
              )}
              <BoutonPoste
                icon={FileText}
                label={t('interview.posteFile')}
                actif={posteMode === 'file'}
                onPress={() => choosePoste('file')}
                testID="iv-poste-file"
              />
              <BoutonPoste
                icon={ClipboardType}
                label={t('interview.postePaste')}
                actif={posteMode === 'paste'}
                onPress={() => choosePoste('paste')}
                testID="iv-poste-paste"
              />
            </View>

            {posteMode === 'file' ? (
              <View style={styles.posteDetail}>
                <View style={styles.offerButtons}>
                  <OutlineIconButton
                    variante="claire"
                    icon={<Ionicons name="document-attach-outline" size={18} color={colors.electricDeep} />}
                    label={t('interview.pickFile')}
                    onPress={pickOfferFile}
                    testID="iv-offer-pick"
                  />
                  <OutlineIconButton
                    variante="claire"
                    icon={<Ionicons name="images-outline" size={18} color={colors.electricDeep} />}
                    label={t('interview.pickCaptures')}
                    onPress={addOfferImages}
                    testID="iv-offer-images"
                  />
                </View>
                {offerFile ? (
                  <Text testID="iv-offer-filename" style={styles.filename}>
                    {offerFile.name}
                  </Text>
                ) : null}
                {offerImages.length > 0 ? (
                  <View testID="iv-offer-thumbs" style={styles.thumbs}>
                    {offerImages.map((img) => (
                      <Image key={img.uri} source={{ uri: img.uri }} style={styles.thumb} />
                    ))}
                  </View>
                ) : null}
              </View>
            ) : null}

            {posteMode === 'paste' ? (
              <View style={styles.posteDetail}>
                <TextInput
                  testID="iv-offer-text"
                  style={[styles.champ, styles.champMultiligne]}
                  placeholder={t('interview.offerPlaceholder')}
                  placeholderTextColor={colors.grey500}
                  multiline
                  value={offerText}
                  onChangeText={setOfferText}
                />
              </View>
            ) : null}

            <Text style={styles.aide}>{t('interview.adaptNote')}</Text>
          </View>

              </>
  );
  const sCompany = (
    <>
{/* Type d'entreprise : facultatif, replié. Il complète le poste, il ne le remplace pas. */}
          <SectionRepliable
            testID="iv-section-company"
            titre={t('interview.companyType')}
            sousTitre={companyType.trim() || undefined}
            icone={<Building2 size={18} color={colors.electricDeep} strokeWidth={2.2} />}
            indicateur="plus"
            ouverte={companyOuverte}
            onToggle={() => setCompanyOuverte((v) => !v)}
          >
            <TextInput
              testID="iv-company"
              style={styles.champ}
              placeholder={t('interview.companyTypePlaceholder')}
              placeholderTextColor={colors.grey500}
              value={companyType}
              onChangeText={setCompanyType}
            />
          </SectionRepliable>

              </>
  );
  const sPrivacy = (
    <>
{/* Mention de confidentialité — trois affirmations vérifiables, aucune promesse implicite.
              PAS « 100 % confidentiel » ni « aucun tiers » : le contenu de l'entretien part chez
              Anthropic, sous-traitant nommé au § 5 de la politique. */}
          <View style={styles.privacy}>
            <Lock size={16} color={colors.grey500} strokeWidth={2} />
            <Text testID="iv-privacy" style={styles.privacyText}>
              Rien n’est partagé avec un recruteur. Tes réponses ne sont pas conservées par
              l’application. Elles sont analysées par Claude (Anthropic), notre sous-traitant, qui ne
              les utilise pas pour entraîner ses modèles.
            </Text>
          </View>
    </>
  );
  const sPied = (
    <>
      {/* Un échec s'affiche ICI, à côté du bouton qui l'a provoqué. */}
      <PiedAction testID="iv-actions" enColonne={large}>
        {localError ? (
          <Text testID="iv-offer-error" style={styles.erreurPied}>
            {localError}
          </Text>
        ) : null}
        {interview.error ? (
          <Text testID="iv-error" style={styles.erreurPied}>
            {interview.error}
          </Text>
        ) : null}
        {!erreur && voixRestante ? (
          <Text testID="iv-voix-restante" style={styles.indice}>
            {texteVoixRestante(voixRestante)}
          </Text>
        ) : null}
        {!erreur && selected && !canStart ? (
          <Text testID="iv-hint" style={styles.indice}>
            {t('interview.hintEmpty')}
          </Text>
        ) : null}
        <GradientButton
          variante="cta"
          hauteur="lg"
          label={t('interview.start')}
          withArrow
          loading={interview.loading}
          disabled={!canStart}
          onPress={begin}
          testID="iv-start"
        />
      </PiedAction>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Flèche du SETUP : dépile vers le sommaire Carrière. À ne pas confondre avec iv-chat-back
          (abandon d'un entretien en cours, avec confirmation) ni iv-fb-back (retour au setup
          depuis le bilan) : les trois coexistent et visent des destinations différentes. */}
      <ScreenHeader
        onBack={onBack}
        title={t('interview.title')}
        variante="carte"
        testID="iv-back"
        droite={
          <OutputLanguagePicker
            value={language}
            onChange={setLanguage}
            label={t('interview.languageLabel')}
            variante="compact"
            testID="iv-language"
          />
        }
      />

      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
                {/* Ordinateur : l'interlocuteur à gauche, le CV et le poste visé à droite avec le bouton de
            lancement. Rien ne défile : tout tient dans la fenêtre. Le téléphone garde sa pile. */}
        {large ? (
          <View testID="iv-colonnes" style={styles.colonnesL}>
            <View style={styles.colGaucheL}>
              {sRole}
              {sPrivacy}
            </View>
            <View style={styles.colDroiteL}>
              {sCv}
              {sPoste}
              {sCompany}
              <View style={styles.pied}>{sPied}</View>
            </View>
          </View>
        ) : (
          <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag">
            {sRole}
            {sCv}
            {sPoste}
            {sCompany}
            {sPrivacy}
          </ScrollView>
        )}
      </KeyboardAvoidingView>
      {large ? null : sPied}
    </SafeAreaView>
  );
}

// ───────────── chat : conversation multi-tours ─────────────
function ChatView({ interview }: { interview: Interview }) {
  const [answer, setAnswer] = useState('');
  // Voix ACTIVÉE PAR DÉFAUT (10/10/2026) ; seul un choix volontaire de la couper est mémorisé.
  // `null` le temps de lire la préférence : rien n'est lu avant de savoir.
  const [soundOn, setSoundOn] = useState<boolean | null>(null);
  useEffect(() => {
    let vivant = true;
    void lirePreferenceVoix().then((active) => {
      if (vivant) setSoundOn(active);
    });
    return () => {
      vivant = false;
    };
  }, []);
  const [recording, setRecording] = useState(false);
  const lastSpokenRef = useRef(-1);
  // Voix ElevenLabs du recruteur (Claire en RH, Aurel en manager), voix du téléphone en repli.
  // La langue de l'entretien pilote le texte ET la voix.
  const voix = useVoixRecruteur(interview.role, interview.language, interview.entretienId);
  const { lire, arreter } = voix;

  // ── TTS : lecture auto du dernier message recruteur, uniquement si le son est activé. La synthèse
  // part dès l'arrivée du texte.
  useEffect(() => {
    if (soundOn !== true) return;
    const idx = interview.messages.length - 1;
    const last = interview.messages[idx];
    if (last && last.role === 'assistant' && idx > lastSpokenRef.current) {
      lastSpokenRef.current = idx;
      lire(last.content, interview.jetonsVoix[idx]);
    }
  }, [interview.messages, interview.jetonsVoix, soundOn, lire]);

  // Arrêt propre en quittant l'écran : la voix (ElevenLabs ou téléphone) par le hook, la reco ici.
  useEffect(() => () => {
    ExpoSpeechRecognitionModule.stop();
  }, []);

  const toggleSound = () => {
    const active = !(soundOn ?? true);
    if (!active) arreter(); // couper arrête immédiatement la lecture en cours
    setSoundOn(active);
    void enregistrerPreferenceVoix(active);
  };

  // Limite de vraie voix atteinte : le son reste, la voix du téléphone lit, et une mention discrète
  // dit quand la voix réaliste revient. La période (semaine ou mois) se lit chez le serveur.
  const raisonVoix = voix.mesure?.raison;
  const [periodeVoix, setPeriodeVoix] = useState<VoixRestante['periode'] | null>(null);
  useEffect(() => {
    if (raisonVoix !== 'plafond') return;
    let vivant = true;
    void lireVoixRestante().then((v) => {
      if (vivant) setPeriodeVoix(v?.periode ?? null);
    });
    return () => {
      vivant = false;
    };
  }, [raisonVoix]);
  const mentionVoix =
    raisonVoix === 'plafond_entretien'
      ? t('interview.voixReviendra.entretien')
      : raisonVoix === 'plafond'
        ? t(periodeVoix === 'semaine' ? 'interview.voixReviendra.lundi' : periodeVoix === 'mois' ? 'interview.voixReviendra.mois' : 'interview.voixReviendra.bientot')
        : null;

  const speakMessage = (text: string, index: number) => lire(text, interview.jetonsVoix[index]);

  // Entretien conclu (12 min) ou arrêté (15 min) : une dictée en cours n'a plus de destinataire.
  useEffect(() => {
    if (!interview.termine || !recording) return;
    ExpoSpeechRecognitionModule.stop();
    setRecording(false);
  }, [interview.termine, recording]);

  // ── STT : la transcription (partielle puis finale) alimente le champ, éditable avant envoi.
  // Chaque résultat porte toute la dictée en cours : il remplace la part dictée, jamais le texte
  // déjà présent au démarrage. Toute saisie à la main coupe le lien, sinon un résultat tardif
  // écraserait la correction.
  const avantDicteeRef = useRef('');
  const ecouteRef = useRef(false);
  useSpeechRecognitionEvent('result', (event) => {
    const transcript = event.results?.[0]?.transcript;
    if (!ecouteRef.current || typeof transcript !== 'string') return;
    const avant = avantDicteeRef.current;
    setAnswer(avant && transcript ? `${avant} ${transcript}` : avant || transcript);
  });
  const finDictee = () => {
    ecouteRef.current = false;
    setRecording(false);
    // Le module laisse la session iOS en enregistrement : on la rend à la lecture dès l'arrêt.
    if (Platform.OS === 'ios') void preparerLecture().catch(() => undefined);
  };
  useSpeechRecognitionEvent('end', finDictee);
  useSpeechRecognitionEvent('error', finDictee);

  const arreterDictee = () => {
    ecouteRef.current = false;
    if (!recording) return;
    ExpoSpeechRecognitionModule.stop();
    setRecording(false);
  };

  const toggleMic = async () => {
    if (recording) {
      // Le lien reste ouvert jusqu'à `end` : l'arrêt rend encore le résultat final.
      ExpoSpeechRecognitionModule.stop();
      setRecording(false);
      return;
    }
    const perm = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
    if (!perm.granted) return;
    avantDicteeRef.current = answer.trim();
    ecouteRef.current = true;
    // Moteur système (pas de on-device forcé) : meilleure fiabilité en français.
    // La dictée écoute la langue de l'entretien, comme la voix la parle.
    // iosCategory : sans lui, le module pose le mode « measurement », qui baisse ensuite le volume
    // de la voix du recruteur. Ignoré sur Android.
    ExpoSpeechRecognitionModule.start({
      lang: interview.language === 'en' ? 'en-US' : 'fr-FR',
      interimResults: true,
      continuous: true,
      iosCategory: { category: 'playAndRecord', categoryOptions: ['defaultToSpeaker', 'allowBluetooth'], mode: 'default' },
    });
    setRecording(true);
  };

  const modifierReponse = (texte: string) => {
    arreterDictee();
    setAnswer(texte);
  };

  const submit = () => {
    if (!answer.trim() || interview.loading) return;
    arreterDictee();
    interview.send(answer);
    setAnswer('');
  };

  // Quitter pendant le chat = perdre la progression → confirmation explicite.
  const confirmQuit = () => {
    confirmer(t('interview.quitTitle'), t('interview.quitBody'), [
      { text: t('interview.quitCancel'), style: 'cancel' },
      { text: t('interview.quitConfirm'), style: 'destructive', onPress: interview.reset },
    ]);
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={confirmQuit} testID="iv-chat-back" />
      <View style={styles.phaseBanner}>
        <View style={styles.phaseGroupe}>
          <View style={styles.phaseDot} />
          <Text testID="iv-phase" style={styles.phaseLabel}>
            {interviewPhaseLabel(interview.userTurns)}
          </Text>
        </View>
        <Pressable
          testID="iv-sound-toggle"
          accessibilityRole="switch"
          accessibilityState={{ checked: soundOn !== false }}
          accessibilityLabel={soundOn !== false ? t('interview.muteVoice') : t('interview.unmuteVoice')}
          onPress={toggleSound}
          style={[styles.soundToggle, soundOn === false && styles.soundToggleOff]}
        >
          <Ionicons
            name={soundOn !== false ? 'volume-high' : 'volume-mute'}
            size={18}
            color={soundOn !== false ? colors.electric : colors.grey500}
          />
          <Text style={[styles.soundToggleText, soundOn === false && styles.soundToggleTextOff]}>
            {soundOn !== false ? t('interview.voixActivee') : t('interview.voixCoupee')}
          </Text>
        </Pressable>
      </View>
      {mentionVoix ? (
        <Text testID="iv-voix-reviendra" style={styles.voixReviendra}>
          {mentionVoix}
        </Text>
      ) : null}
      {!estProduction() && voix.mesure ? <IndicateurVoix mesure={voix.mesure} /> : null}

      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.chatContent} keyboardShouldPersistTaps="handled">
          {interview.messages.map((m, i) => {
            const isRecruiter = m.role === 'assistant';
            return (
              <View key={i}>
                <MessageBubble
                  role={isRecruiter ? 'recruiter' : 'user'}
                  text={m.content}
                  testID={isRecruiter ? `iv-msg-recruiter-${i}` : `iv-msg-user-${i}`}
                />
                {isRecruiter ? (
                  <Pressable
                    testID={`iv-speak-${i}`}
                    accessibilityRole="button"
                    accessibilityLabel={t('a11y.listen')}
                    onPress={() => speakMessage(m.content, i)}
                    hitSlop={8}
                    style={styles.speakBtn}
                  >
                    <Ionicons name="volume-high" size={15} color={colors.grey500} />
                    <Text style={styles.speakText}>{t('interview.listen')}</Text>
                  </Pressable>
                ) : null}
              </View>
            );
          })}

          {interview.loading ? (
            <Text testID="iv-thinking" style={styles.thinking}>
              L’IA réfléchit…
            </Text>
          ) : null}

          {interview.error ? (
            <View testID="iv-chat-error" style={styles.field}>
              <Text style={styles.error}>{interview.error}</Text>
              <Button title={t('common.retry')} variant="secondary" onPress={interview.retry} testID="iv-retry" />
            </View>
          ) : null}
        </ScrollView>

        {interview.canFinish && !interview.termine ? (
          <Pressable
            testID="iv-finish"
            onPress={interview.finish}
            disabled={interview.loading}
            hitSlop={8}
            style={styles.finishLink}
          >
            <Text style={styles.finishText}>{t('interview.finish')}</Text>
          </Pressable>
        ) : null}

        {recording ? (
          <Text testID="iv-recording" style={styles.recordingHint}>
            🔴 Enregistrement… parle, puis touche le carré pour arrêter.
          </Text>
        ) : null}

        {interview.termine ? (
          <View style={styles.composer}>
            <View style={styles.flex}>
              <Button title={t('interview.finish')} onPress={interview.finish} disabled={interview.loading} testID="iv-finish" />
            </View>
          </View>
        ) : (
          <>
            {answer ? (
              <Pressable
                testID="iv-clear"
                accessibilityRole="button"
                onPress={() => modifierReponse('')}
                hitSlop={8}
                style={styles.clearLink}
              >
                <Text style={styles.finishText}>{t('interview.clearAnswer')}</Text>
              </Pressable>
            ) : null}
            <View style={styles.composer}>
              <TextInput
                testID="iv-answer"
                style={[styles.input, styles.answerInput]}
                placeholder={t('interview.answerPlaceholder')}
                placeholderTextColor={colors.grey400}
                multiline
                value={answer}
                onChangeText={modifierReponse}
              />
              <Pressable
                testID="iv-mic"
                accessibilityRole="button"
                accessibilityState={{ busy: recording }}
                accessibilityLabel={recording ? t('interview.stopDictation') : t('interview.startDictation')}
                onPress={toggleMic}
                hitSlop={8}
                style={[styles.micBtn, recording && styles.micBtnActive]}
              >
                <Ionicons name={recording ? 'stop' : 'mic'} size={22} color={recording ? colors.white : colors.electric} />
              </Pressable>
              <Button title={t('interview.send')} onPress={submit} disabled={interview.loading} testID="iv-send" />
            </View>
          </>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// ───────────── bilan final ─────────────
// Repère du bilan (interlocuteur, durée, langue) : une pastille de texte, rien de cliquable.
function Repere({ label, testID }: { label: string; testID: string }) {
  return (
    <View testID={testID} style={styles.repere}>
      <Text style={styles.repereTexte}>{label}</Text>
    </View>
  );
}

function FeedbackView({ interview }: { interview: Interview }) {
  const fb = interview.feedback;
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurPdf, setErreurPdf] = useState(false);
  const telechargerPdf = async () => {
    if (!fb || exportEnCours) return;
    setExportEnCours(true);
    setErreurPdf(false);
    try {
      await exporterBilanPdf({
        feedback: fb,
        langue: interview.language,
        role: interview.role,
        posteVise: interview.posteVise,
        aOffre: interview.aOffre,
        dureeMinutes: interview.dureeMinutes,
        date: new Date(),
      });
    } catch {
      setErreurPdf(true);
    } finally {
      setExportEnCours(false);
    }
  };
  const langue = OUTPUT_LANGUAGES.find((o) => o.value === interview.language)?.label ?? interview.language;
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View testID="iv-fb-header">
        <ScreenHeader variante="carte" onBack={interview.reset} title={t('interview.feedbackHeader')} testID="iv-fb-back" />
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        {fb ? (
          <>
            {/* Ce que la session a été, sans rien deviner : l'interlocuteur choisi, la durée mesurée
                (absente si inconnue) et la langue du bilan. */}
            <View testID="iv-fb-meta" style={styles.reperes}>
              <Repere testID="iv-fb-meta-role" label={t(`interview.interlocutors.${interview.role}.label`)} />
              {interview.dureeMinutes ? (
                <Repere testID="iv-fb-meta-duree" label={t('interview.dureeMin', { n: interview.dureeMinutes })} />
              ) : null}
              <Repere testID="iv-fb-meta-langue" label={langue} />
            </View>

            {/* Le serveur ne renvoie qu'un paragraphe : pas de titre inventé au-dessus. */}
            {fb.overall ? (
              <FondNuit testID="iv-fb-overall" style={styles.carteNuit}>
                <Text style={styles.nuitEtiquette}>{t('interview.overallLabel')}</Text>
                <Text style={styles.nuitTexte}>{fb.overall}</Text>
              </FondNuit>
            ) : null}

            <FeedbackSection
              status="success"
              icon={CircleCheck}
              title={t('interview.strengths')}
              items={fb.strengths}
              testID="iv-fb-strengths"
            />
            <FeedbackSection
              status="warning"
              icon={TriangleAlert}
              title={t('interview.improvements')}
              items={fb.improvements}
              testID="iv-fb-improvements"
            />
            <FeedbackSection
              status="info"
              icon={Lightbulb}
              title={t('interview.tips')}
              items={fb.tips}
              testID="iv-fb-tips"
            />
          </>
        ) : null}
      </ScrollView>

      <PiedAction testID="iv-fb-actions">
        {erreurPdf ? (
          <Text testID="iv-fb-pdf-erreur" style={styles.erreurPied}>
            {t('interview.pdfErreur')}
          </Text>
        ) : null}
        {/* Masqué si le binaire ne sait pas produire de PDF (module natif absent) : jamais un bouton mort. */}
        {fb && pdfBilanDisponible() ? (
          <GradientButton
            variante="cta"
            hauteur="lg"
            label={t('interview.downloadPdf')}
            onPress={() => void telechargerPdf()}
            loading={exportEnCours}
            testID="iv-fb-pdf"
          />
        ) : null}
        <Button title={t('interview.restart')} variant="secondary" onPress={interview.reset} testID="iv-restart" />
      </PiedAction>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesL: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  pied: { marginTop: spacing.sm },
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  content: { padding: screenPadding, paddingTop: spacing.sm, gap: spacing.md },

  field: { gap: spacing.xs },
  error: { color: colors.error, fontSize: typography.small.size },
  input: {
    minHeight: 48,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    fontSize: typography.body.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },

  // ── Refonte du setup : cartes claires, champ poste à deux icônes, pied fixe ──
  etiquette: {
    fontSize: typography.caption.size + 1,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.2,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  carte: { borderRadius: formes.rayon.lg, backgroundColor: colors.white, padding: spacing.md, gap: spacing.sm, ...shadow.card },
  vide: { fontSize: typography.body.size, color: colors.grey500 },

  // CV : la ligne du CV utilisé, puis la liste quand on touche « Changer ».
  cvLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, minHeight: formes.cible },
  cvLigneListe: { borderTopWidth: 1, borderTopColor: colors.grey200, paddingTop: spacing.sm },
  cvIcone: {
    width: 36,
    height: 36,
    borderRadius: formes.rayon.sm,
    backgroundColor: FEATURE_ACCENTS.interview.tint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cvNom: { flex: 1, fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  // ENCRE et non accent : l'accent de l'entretien affiche 3,09:1 sur blanc, sous le seuil de 4,5
  // exigé pour un texte courant. L'encre est la même identité, assombrie jusqu'à devenir lisible.
  ajouterCv: { flex: 1, fontSize: typography.bodyMd.size, color: FEATURE_ACCENTS.interview.ink, fontWeight: '700' },
  changer: {
    minHeight: formes.cible,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.blueSoft,
  },
  changerTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.electricInk },
  radio: {
    width: 22,
    height: 22,
    borderRadius: radius.pill,
    borderWidth: 2,
    borderColor: colors.grey400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOn: { backgroundColor: FEATURE_ACCENTS.interview.ink, borderColor: FEATURE_ACCENTS.interview.ink },

  // Poste visé : un champ et deux boutons-icônes sur la même ligne.
  posteLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  champ: {
    minHeight: formes.hauteur.md,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.md,
    fontSize: typography.body.size,
    fontWeight: '500',
    color: colors.night,
    backgroundColor: colors.grey50,
  },
  champFlex: { flex: 1, minWidth: 0 },
  champMode: { justifyContent: 'center', backgroundColor: colors.blueSoft, borderColor: colors.bordureClaire },
  champModeTexte: { fontSize: typography.body.size, fontWeight: '700', color: colors.electricInk },
  champMultiligne: { minHeight: 120, paddingTop: spacing.md, textAlignVertical: 'top' },
  boutonPoste: {
    width: formes.cible,
    height: formes.hauteur.md,
    borderRadius: radius.lg,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boutonPosteActif: { backgroundColor: FEATURE_ACCENTS.interview.ink },
  posteDetail: { gap: spacing.sm },
  // Fiche de poste importée : boutons contour clair + rendu fichier/vignettes.
  offerButtons: { flexDirection: 'row', gap: spacing.sm },
  filename: { fontSize: typography.bodyMd.size, color: colors.electricInk },
  thumbs: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  thumb: { width: 56, height: 56, borderRadius: radius.sm, backgroundColor: colors.grey200 },
  aide: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },

  // Mention de confidentialité (cadenas) et pied d'action.
  privacy: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, marginTop: spacing.xs },
  privacyText: { flex: 1, fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  indice: { textAlign: 'center', fontSize: 12.5, color: colors.grey500 },
  erreurPied: { textAlign: 'center', fontSize: typography.small.size, color: colors.error },

  // chat
  // Phase à gauche, pastille de voix à droite : deux blocs qui ne se chevauchent jamais, même en
  // anglais sur un écran étroit.
  phaseBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: screenPadding,
    backgroundColor: colors.blueSoft,
  },
  phaseGroupe: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexShrink: 1 },
  phaseDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.bluePrimary },
  phaseLabel: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    textTransform: 'uppercase',
    color: colors.bluePrimary,
  },
  chatContent: { padding: screenPadding, gap: spacing.md },
  thinking: { alignSelf: 'flex-start', color: colors.grey500, fontSize: typography.small.size, fontStyle: 'italic' },
  composer: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-end', paddingHorizontal: screenPadding, paddingBottom: spacing.sm },
  answerInput: { flex: 1, minHeight: 44, maxHeight: 120, textAlignVertical: 'top', paddingVertical: spacing.sm },
  finishLink: { alignSelf: 'center', paddingVertical: spacing.sm },
  clearLink: { alignSelf: 'flex-end', paddingHorizontal: screenPadding, paddingBottom: spacing.xs },
  finishText: { fontSize: typography.small.size, fontWeight: typography.bodyMd.weight, color: colors.grey500, textDecorationLine: 'underline' },

  // voix
  // Pastille de 44 pt au moins (cible tactile), dans la barre de l'entretien.
  soundToggle: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
  },
  soundToggleOff: { backgroundColor: colors.grey100 },
  soundToggleText: { fontSize: typography.small.size, fontWeight: typography.bodyMd.weight, color: colors.electric },
  soundToggleTextOff: { color: colors.grey500 },
  voixReviendra: { fontSize: typography.caption.size, color: colors.grey500, textAlign: 'center', paddingTop: spacing.xs },
  speakBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs / 2, alignSelf: 'flex-start', marginTop: spacing.xs / 2, paddingVertical: spacing.xs / 2 },
  speakText: { fontSize: typography.small.size, color: colors.grey500 },
  micBtn: { width: 44, height: 44, borderRadius: radius.pill, borderWidth: 1.5, borderColor: colors.electric, alignItems: 'center', justifyContent: 'center' },
  micBtnActive: { backgroundColor: colors.error, borderColor: colors.error },
  recordingHint: { paddingHorizontal: screenPadding, paddingBottom: spacing.xs, fontSize: typography.small.size, color: colors.error },

  // bilan
  reperes: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  repere: {
    minHeight: formes.hauteur.sm - 8,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  repereTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.night },
  carteNuit: { borderRadius: formes.rayon.lg, padding: spacing.lg, gap: spacing.sm },
  nuitEtiquette: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.4,
    textTransform: 'uppercase',
    color: colors.blancAtenue,
  },
  nuitTexte: { fontSize: typography.bodyMd.size, lineHeight: typography.body.lineHeight + 2, fontWeight: '700', color: colors.white },
});
